import {
  deepEqual,
  type LayoutItem,
  type ReportDefinition,
  type ReportOperation,
  type Widget,
  type WidgetPatch
} from '@tallyframe/core';

/**
 * Expresses "make definition A look like definition B" as report operations.
 *
 * Undo and redo are the reason this exists. The edit loop normally sends the
 * exact operations the renderer emitted, but an undo jumps the local definition
 * backwards, and the accumulated operation list no longer describes the
 * difference between what the server has stored and what is on screen. Rather
 * than invent inverse operations — `remove_widget` has no inverse that does not
 * carry the whole widget — the pending batch is recomputed as a diff against
 * the last definition the server confirmed.
 *
 * There is no `set_definition` operation on purpose: an agent cannot reach a
 * shape the editor could not produce, and neither can this.
 */

// Re-exported for existing importers of `./diff`.
export { deepEqual };

/** Fields a `WidgetPatch` may carry, by widget type. Mirrors core's own allow
 *  list in `applyPatch` — sending a field the target type does not accept is a
 *  rejected batch, not a silently ignored key. */
const PATCHABLE: Record<Widget['type'], Array<keyof WidgetPatch>> = {
  kpi: ['title', 'caption', 'metric', 'format'],
  chart: ['title', 'chartType', 'dimensionColumnId', 'series', 'format', 'stacked', 'maxCategories'],
  table: ['title', 'columns', 'sort', 'pageSize', 'groupBy', 'defaultExpandedDepth'],
  text: ['title', 'body']
};

/** Fields that can be *removed* rather than only changed. Core reads `null` as
 *  "delete this key". `defaultExpandedDepth` is not here — it is not nullable
 *  in core, same as `pageSize`. */
const NULLABLE: ReadonlySet<string> = new Set(['caption', 'sort', 'groupBy']);

function widgetPatch(from: Widget, to: Widget): WidgetPatch | null {
  if (from.type !== to.type) return null;
  const before = from as unknown as Record<string, unknown>;
  const after = to as unknown as Record<string, unknown>;
  const patch: Record<string, unknown> = {};

  for (const field of PATCHABLE[to.type]) {
    const previous = before[field];
    const next = after[field];
    if (deepEqual(previous, next)) continue;
    if (next === undefined) {
      if (NULLABLE.has(field)) patch[field] = null;
      continue;
    }
    patch[field] = next;
  }

  return Object.keys(patch).length > 0 ? (patch as WidgetPatch) : null;
}

function layoutOf(definition: ReportDefinition, widgetId: string): LayoutItem | undefined {
  return definition.layout.items.find((item) => item.widgetId === widgetId);
}

export function diffDefinitions(from: ReportDefinition, to: ReportDefinition): ReportOperation[] {
  const operations: ReportOperation[] = [];

  if (from.title !== to.title) {
    operations.push({ op: 'rename_report', title: to.title });
  }

  // Detect changes in report details (subtitle, preparedFor)
  // Normalize by trimming to match core's apply semantics: empty after trim means clear
  const normalize = (value?: string | null) => (value ?? '').trim();
  const details: { subtitle?: string | null; preparedFor?: string | null } = {};
  if (normalize(from.subtitle) !== normalize(to.subtitle)) {
    details.subtitle = normalize(to.subtitle) || null;
  }
  if (normalize(from.preparedFor) !== normalize(to.preparedFor)) {
    details.preparedFor = normalize(to.preparedFor) || null;
  }
  if (Object.keys(details).length > 0) {
    operations.push({ op: 'set_report_details', ...details });
  }

  const before = new Map(from.widgets.map((widget) => [widget.id, widget]));
  const after = new Map(to.widgets.map((widget) => [widget.id, widget]));

  for (const widget of from.widgets) {
    if (!after.has(widget.id)) operations.push({ op: 'remove_widget', widgetId: widget.id });
  }

  for (const widget of to.widgets) {
    const previous = before.get(widget.id);
    if (!previous) {
      const placement = layoutOf(to, widget.id);
      // The id travels with the operation. A widget that changed identity on
      // the way to the server would lose its selection and break the undo
      // stack the moment the saved definition came back.
      operations.push(
        placement
          ? {
              op: 'add_widget',
              widget,
              layout: { x: placement.x, y: placement.y, w: placement.w, h: placement.h }
            }
          : { op: 'add_widget', widget }
      );
      continue;
    }
    if (previous.type !== widget.type) {
      // Core refuses to change a widget's type in place, so express it the way
      // a person would: remove the old one, add the new one under a new id.
      operations.push({ op: 'remove_widget', widgetId: widget.id });
      operations.push({ op: 'add_widget', widget });
      continue;
    }
    const patch = widgetPatch(previous, widget);
    if (patch) operations.push({ op: 'update_widget', widgetId: widget.id, patch });
  }

  if (!deepEqual(from.layout.items, to.layout.items)) {
    operations.push({ op: 'set_layout', items: to.layout.items });
  }

  if (!deepEqual(from.print, to.print)) {
    operations.push({ op: 'set_print_options', print: to.print });
  }

  return operations;
}
